import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useLocation } from "wouter";
import { ventasAPI } from "@/lib/api";
import { dataRefresh } from "@/lib/dataRefresh";
import { appDataStore } from "@/lib/appDataStore";
import { prefetchModuleChunkByRoute } from "@/lib/moduleChunkPrefetch";
import { toast } from "sonner";
import { Navbar } from "./Navbar";
import { UserData } from "./LoginPage";
import { availableModules, getPermittedModulesForUser, Module, OpenModule } from "@/types/modules";
import { getModulePermissionLevel, getSectionPermissionLevel, MODULE_PRESUPUESTOS, sessionCanAccessModule, sessionCanFetchFacturas, sessionIsElevated } from "@/lib/modulePermissions";
import { peekDocFocus, subscribeDocFocus } from "@/lib/docFocus";
import {
  appendOpenModuleIfAbsent,
  parseOpenModuleIds,
  uniqueOpenModules,
} from "@/lib/openModules";
import { PerfilPage } from "./PerfilPage";
import { ModuleSuspenseFallback } from "./ModuleSuspenseFallback";
import { DashboardOperativo } from "./DashboardOperativo";

const ProveedoresModule = lazy(() =>
  import("@/modules/ProveedoresModule").then((m) => ({ default: m.ProveedoresModule }))
);
const ClientesModule = lazy(() =>
  import("@/modules/ClientesModule").then((m) => ({ default: m.ClientesModule }))
);
const VentasModule = lazy(() =>
  import("@/modules/VentasModule").then((m) => ({ default: m.VentasModule }))
);
const ChequesModule = lazy(() =>
  import("@/modules/ChequesModule").then((m) => ({ default: m.ChequesModule }))
);
const StockModule = lazy(() =>
  import("@/modules/StockModule").then((m) => ({ default: m.StockModule }))
);
const ArticulosModule = lazy(() =>
  import("@/modules/ArticulosModule").then((m) => ({ default: m.ArticulosModule }))
);
const OpcionesModule = lazy(() =>
  import("@/modules/OpcionesModule").then((m) => ({ default: m.OpcionesModule }))
);
const CajaBancosModule = lazy(() =>
  import("@/modules/CajaBancosModule").then((m) => ({ default: m.CajaBancosModule }))
);
const PresupuestosModule = lazy(() =>
  import("@/modules/PresupuestosModule").then((m) => ({ default: m.PresupuestosModule }))
);
const FondosModule = lazy(() =>
  import("@/modules/FondosModule").then((m) => ({ default: m.FondosModule }))
);
const RecibosModule = lazy(() =>
  import("@/modules/RecibosModule").then((m) => ({ default: m.RecibosModule }))
);
const RemitosVtasModule = lazy(() =>
  import("@/modules/RemitosVtasModule").then((m) => ({ default: m.RemitosVtasModule }))
);
const NotificationsModule = lazy(() =>
  import("@/modules/NotificationsModule").then((m) => ({ default: m.NotificationsModule }))
);
const ReportesModule = lazy(() =>
  import("@/modules/ReportesModule").then((m) => ({ default: m.ReportesModule }))
);
const IvaModule = lazy(() => import("@/modules/IvaModule").then((m) => ({ default: m.IvaModule })));
const PedidosModule = lazy(() =>
  import("@/modules/PedidosModule").then((m) => ({ default: m.PedidosModule }))
);

const FOCUS_REFRESH_TTL_MS = 60_000;

interface HomePageProps {
  user: UserData;
  onLogout: () => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  onUserUpdated?: (user: UserData) => void;
  presupuestos: any[];
  setPresupuestos: React.Dispatch<React.SetStateAction<any[]>>;
  facturas: any[];
  setFacturas: React.Dispatch<React.SetStateAction<any[]>>;
  movimientos: any[];
  setMovimientos: React.Dispatch<React.SetStateAction<any[]>>;
  cierres: any[];
  setCierres: React.Dispatch<React.SetStateAction<any[]>>;
}

export function HomePage({
  user,
  onLogout,
  isDarkMode,
  toggleDarkMode,
  onUserUpdated,
  presupuestos,
  setPresupuestos,
  facturas,
  setFacturas,
  movimientos,
  setMovimientos,
  cierres,
  setCierres
}: HomePageProps) {
  const userAllowedModules = getPermittedModulesForUser(user.modulosPermitidos, {
    isElevated: sessionIsElevated(user),
  });
  const sessionModulePermissions = sessionIsElevated(user)
    ? undefined
    : (user.modulePermissions ?? []);

  const [openModules, setOpenModules] = useState<OpenModule[]>(() => {
    try {
      const savedModuleIds = parseOpenModuleIds(
        localStorage.getItem(`openModules_${user.id}`),
      );
      return savedModuleIds.flatMap((id) => {
        const module = availableModules.find((m) => m.id === id);
        if (module && sessionCanAccessModule(user, id)) {
          return [{ id, module }];
        }
        return [];
      });
    } catch (e) {
      console.error('Error loading openModules from localStorage:', e);
    }
    return [];
  });

  const [location, setLocation] = useLocation();
  const [showPerfil, setShowPerfil] = useState(false);
  const [remitosRefresh, _setRemitosRefresh] = useState(0);
  const [focusRefreshRequest, setFocusRefreshRequest] = useState<{ route: string; token: number }>({ route: "", token: 0 });

  const mountedModuleIdsRef = useRef<Set<number>>(new Set());
  const lastFocusRefreshRef = useRef(Date.now());

  useEffect(() => {
    const unique = uniqueOpenModules(openModules);
    if (unique.length !== openModules.length) {
      setOpenModules(unique);
      return;
    }
    localStorage.setItem(
      `openModules_${user.id}`,
      JSON.stringify(unique.map((m) => m.id)),
    );
  }, [openModules, user.id]);

  const getModuleForRoute = (path: string) => {
    const base = path.split('?')[0];
    return availableModules.find(m => m.route === base || base.startsWith(m.route + '/'));
  };

  useEffect(() => {
    if (location && location !== '/') {
      const module = getModuleForRoute(location);
      if (module && sessionCanAccessModule(user, module.id)) {
        setOpenModules((prev) =>
          appendOpenModuleIfAbsent(prev, { id: module.id, module }),
        );
        localStorage.setItem(`currentRoute_${user.id}`, location);
      }
    }
  }, [location, user.modulosPermitidos, user.isSuperuser, user.is_superuser, user.id]);

  useEffect(() => {
    const savedRoute = localStorage.getItem(`currentRoute_${user.id}`);
    if (savedRoute && savedRoute !== '/' && location === '/') {
      const module = getModuleForRoute(savedRoute);
      if (module && sessionCanAccessModule(user, module.id)) {
        setOpenModules((prev) =>
          appendOpenModuleIfAbsent(prev, { id: module.id, module }),
        );
        setLocation(savedRoute);
      }
    }
  }, []); // Run only once on mount

  useEffect(() => {
    const permittedIds = new Set(
      sessionIsElevated(user)
        ? availableModules.map((m) => m.id)
        : (user.modulosPermitidos || []).map((id) => Number(id)),
    );
    const permittedOpenModules = openModules.filter((m) =>
      permittedIds.has(Number(m.id)),
    );

    if (permittedOpenModules.length !== openModules.length) {
      const removedIds = new Set(
        openModules
          .filter((m) => !permittedIds.has(Number(m.id)))
          .map((m) => m.id)
      );
      removedIds.forEach((id) => mountedModuleIdsRef.current.delete(id));
      setOpenModules(permittedOpenModules);
      toast.info("Se actualizaron tus permisos. Algunos módulos ya no están disponibles.");
    }

    const [activePath] = location.split("?");
    const activeModule = getModuleForRoute(activePath);
    if (activeModule && !permittedIds.has(Number(activeModule.id))) {
      setShowPerfil(false);
      setLocation("/");
      localStorage.setItem(`currentRoute_${user.id}`, "/");
    }
  }, [user.modulosPermitidos, user.isSuperuser, user.is_superuser, openModules, location, setLocation, user.id]);

  useEffect(() => {
    const handlePresupuestosRefresh = () => {
      if (!sessionCanAccessModule(user, MODULE_PRESUPUESTOS)) return;
      ventasAPI.listPresupuestos().then(setPresupuestos).catch(console.error);
    };

    const handleFacturasRefresh = () => {
      if (!sessionCanFetchFacturas(user)) return;
      appDataStore.refreshFacturas()
        .then((freshFacturas) => {
          setFacturas(freshFacturas);
          dataRefresh.emit('notifications');
        })
        .catch(console.error);
    };

    dataRefresh.on('presupuestos', handlePresupuestosRefresh);
    dataRefresh.on('ventas', handleFacturasRefresh);
    dataRefresh.on('facturas', handleFacturasRefresh);

    return () => {
      dataRefresh.off('presupuestos', handlePresupuestosRefresh);
      dataRefresh.off('ventas', handleFacturasRefresh);
      dataRefresh.off('facturas', handleFacturasRefresh);
    };
  }, [user]);

  useEffect(() => {
    const refreshActiveModule = () => {
      if (document.visibilityState === "hidden") return;

      const now = Date.now();
      if (now - lastFocusRefreshRef.current < FOCUS_REFRESH_TTL_MS) return;
      lastFocusRefreshRef.current = now;

      const [activeRoute] = location.split("?");

      switch (activeRoute) {
        case "/ventas":
          dataRefresh.emit("ventas");
          dataRefresh.emit("presupuestos");
          break;
        case "/presupuestos":
          dataRefresh.emit("presupuestos");
          break;
        case "/clientes":
          dataRefresh.emit("ventas");
          dataRefresh.emit("facturas");
          setFocusRefreshRequest({ route: activeRoute, token: now });
          break;
        case "/remitos-vtas":
        case "/stock":
        case "/caja-bancos":
        case "/recibos":
        case "/fondos":
          setFocusRefreshRequest({ route: activeRoute, token: now });
          break;
        case "/notifications":
          dataRefresh.emit("notifications");
          break;
      }
    };

    window.addEventListener("focus", refreshActiveModule);
    document.addEventListener("visibilitychange", refreshActiveModule);

    return () => {
      window.removeEventListener("focus", refreshActiveModule);
      document.removeEventListener("visibilitychange", refreshActiveModule);
    };
  }, [location]);

  const handlePresupuestoCreated = (presupuesto: any) => {
    setPresupuestos([...presupuestos, presupuesto]);
  };

  const handlePresupuestoDeleted = (numero: string) => {
    setPresupuestos((prev) => prev.filter((p) => p.numero !== numero));
  };

  const handleMovimientosUpdate = (updatedMovimientos: any[]) => {
    setMovimientos(updatedMovimientos);
  };

  const handleCierresUpdate = (updatedCierres: any[]) => {
    setCierres(updatedCierres);
  };

  const openModule = (module: Module, navigateTo?: string) => {
    setShowPerfil(false);
    const path = navigateTo ?? module.route;
    prefetchModuleChunkByRoute(path);
    setOpenModules((prev) =>
      appendOpenModuleIfAbsent(prev, { id: module.id, module }),
    );
    setLocation(path);
    localStorage.setItem(`currentRoute_${user.id}`, path);
  };

  const openModuleByRoute = (route: string) => {
    prefetchModuleChunkByRoute(route);
    const [baseRoute] = route.split("?");
    const module = getModuleForRoute(baseRoute);
    if (module && sessionCanAccessModule(user, module.id)) {
      openModule(module, route);
    } else {
      setShowPerfil(false);
      setLocation(route);
      localStorage.setItem(`currentRoute_${user.id}`, route);
    }
  };
  const openModuleByRouteRef = useRef(openModuleByRoute);
  openModuleByRouteRef.current = openModuleByRoute;

  useEffect(() => subscribeDocFocus(() => {
    const focus = peekDocFocus();
    if (!focus) return;
    if (focus.kind === "factura") openModuleByRouteRef.current("/ventas");
    if (focus.kind === "remito") openModuleByRouteRef.current("/remitos-vtas");
  }), []);

  const closeModule = (moduleId: number) => {
    setOpenModules((prev) => prev.filter((m) => Number(m.id) !== Number(moduleId)));
    mountedModuleIdsRef.current.delete(moduleId);
    const closingModule = openModules.find(m => m.id === moduleId);
    if (closingModule && location === closingModule.module.route) {
      setShowPerfil(false);
      setLocation('/');
      localStorage.setItem(`currentRoute_${user.id}`, '/');
    }
  };

  const reorderOpenModules = (newOrder: OpenModule[]) => {
    setOpenModules(uniqueOpenModules(newOrder));
  };

  const renderModuleContent = () => {
    if (showPerfil) {
      return (
        <PerfilPage
          user={user}
          movimientos={movimientos}
          onBack={() => setShowPerfil(false)}
          onUserUpdated={onUserUpdated}
        />
      );
    }

    if (location === "/notifications") {
      return (
        <Suspense fallback={<ModuleSuspenseFallback />}>
          <NotificationsModule user={user} openModuleByRoute={openModuleByRoute} />
        </Suspense>
      );
    }

    return (
      <>
        {location === "/" && (
          <DashboardOperativo
            modulosPermitidos={user.modulosPermitidos || []}
            modulePermissions={sessionModulePermissions}
            userRol={user.rol}
            isElevated={sessionIsElevated(user)}
            openModuleByRoute={openModuleByRoute}
          />
        )}

        {/* Lazy-mount + keep-alive: mount on first activation, then hide with CSS when switching away. */}
        {openModules.map((openModule) => {
          const locationBase = location.split('?')[0];
          const isActive =
            locationBase === openModule.module.route ||
            location === openModule.module.route ||
            (openModule.module.route !== '/' && location.startsWith(openModule.module.route + '/'));

          if (isActive) {
            mountedModuleIdsRef.current.add(openModule.id);
          }

          // Only render if this module has been activated at least once (lazy mount)
          if (!mountedModuleIdsRef.current.has(openModule.id)) return null;

          const permission = sessionIsElevated(user)
            ? "full"
            : getModulePermissionLevel(
                user.modulePermissions ?? [],
                openModule.module.id,
              );
          const isReadOnly = permission !== "full";
          const configuradorLevel = sessionIsElevated(user)
            ? "full"
            : getSectionPermissionLevel(
                user.modulePermissions ?? [],
                6,
                "configurador",
              );

          return (
            <Suspense key={openModule.id} fallback={<ModuleSuspenseFallback />}>
              <div
                className="flex min-h-0 flex-1 flex-col"
                style={isActive ? undefined : { display: 'none' }}
              >
                {openModule.module.route === "/proveedores" && (
                  <ProveedoresModule
                    isReadOnly={isReadOnly}
                    isActive={isActive}
                    modulePermissions={sessionModulePermissions}
                  />
                )}
                {openModule.module.route === "/clientes" && (
                  <ClientesModule
                    user={user}
                    facturas={facturas}
                    isReadOnly={isReadOnly}
                    isActive={isActive}
                    focusRefreshToken={focusRefreshRequest.route === "/clientes" && isActive ? focusRefreshRequest.token : 0}
                    modulePermissions={sessionModulePermissions}
                  />
                )}
                {openModule.module.route === "/ventas" && (
                  <VentasModule
                    user={user}
                    presupuestos={presupuestos}
                    facturasCache={facturas}
                    cierresCache={cierres}
                    isReadOnly={isReadOnly}
                    isActive={isActive}
                    modulePermissions={sessionModulePermissions}
                  />
                )}
                {openModule.module.route === "/cheques" && <ChequesModule isReadOnly={isReadOnly} />}
                {openModule.module.route === "/stock" && (
                  <StockModule
                    isReadOnly={isReadOnly}
                    isActive={isActive}
                    focusRefreshToken={focusRefreshRequest.route === "/stock" && isActive ? focusRefreshRequest.token : 0}
                    canAccessConfigurador={configuradorLevel !== "none"}
                    configuradorReadOnly={configuradorLevel !== "full"}
                    modulePermissions={sessionModulePermissions}
                  />
                )}
                {openModule.module.route === "/articulos" && <ArticulosModule isReadOnly={isReadOnly} isActive={isActive} />}
                {openModule.module.route === "/caja-bancos" && (
                  <CajaBancosModule
                    user={user}
                    facturas={facturas}
                    movimientos={movimientos}
                    onMovimientosUpdate={handleMovimientosUpdate}
                    cierres={cierres}
                    onCierresUpdate={handleCierresUpdate}
                    isReadOnly={isReadOnly}
                    isActive={isActive}
                    focusRefreshToken={focusRefreshRequest.route === "/caja-bancos" && isActive ? focusRefreshRequest.token : 0}
                    modulePermissions={sessionModulePermissions}
                  />
                )}
                {openModule.module.route === "/opciones" && (
                  <OpcionesModule
                    isReadOnly={isReadOnly}
                    isDarkMode={isDarkMode}
                    userId={user.id}
                    modulePermissions={sessionModulePermissions}
                  />
                )}
                {openModule.module.route === "/presupuestos" && (
                  <PresupuestosModule
                    user={user}
                    presupuestos={presupuestos}
                    onPresupuestoCreated={handlePresupuestoCreated}
                    onPresupuestoDeleted={handlePresupuestoDeleted}
                    isReadOnly={isReadOnly}
                    isActive={isActive}
                  />
                )}
                {openModule.module.route === "/fondos" && (
                  <FondosModule
                    isReadOnly={isReadOnly}
                    isActive={isActive}
                    focusRefreshToken={focusRefreshRequest.route === "/fondos" && isActive ? focusRefreshRequest.token : 0}
                  />
                )}
                {openModule.module.route === "/recibos" && (
                  <RecibosModule
                    user={user}
                    isReadOnly={isReadOnly}
                    isActive={isActive}
                    focusRefreshToken={focusRefreshRequest.route === "/recibos" && isActive ? focusRefreshRequest.token : 0}
                  />
                )}
                {openModule.module.route === "/remitos-vtas" && (
                  <RemitosVtasModule
                    user={user}
                    isReadOnly={isReadOnly}
                    refreshTrigger={remitosRefresh}
                    isActive={isActive}
                    focusRefreshToken={focusRefreshRequest.route === "/remitos-vtas" && isActive ? focusRefreshRequest.token : 0}
                  />
                )}
                {openModule.module.route === "/reportes" && (
                  <ReportesModule isReadOnly={isReadOnly} user={user} />
                )}
                {openModule.module.route === "/iva" && <IvaModule isReadOnly={isReadOnly} />}
                {openModule.module.route === "/pedidos" && (
                  <PedidosModule user={user} isReadOnly={isReadOnly} isActive={isActive} />
                )}
                {openModule.module.route === "/notifications" && (
                  <NotificationsModule user={user} openModuleByRoute={openModuleByRoute} />
                )}
                {!["proveedores", "clientes", "ventas", "cheques", "stock", "articulos", "caja-bancos", "opciones", "presupuestos", "fondos", "recibos", "remitos-vtas", "reportes", "iva", "pedidos", "notifications"].includes(openModule.module.route.replace('/', '')) && (
                  <div className="flex-1 p-8">
                    <div className="max-w-7xl mx-auto">
                      <div className="p-8 text-center">
                        <p className="text-muted-foreground">Módulo en desarrollo</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Suspense>
          );
        })}
      </>
    );
  };

  // Módulos abiertos: viewport fijo (h-dvh) + main scrolleable bajo el Navbar.
  // Módulos con scroll interno (Configurador, editor PDF) usan flex-1 min-h-0 overflow-hidden
  // y llenan el main sin forzar scrollbar de página. En home: min-h-screen + scroll del dashboard.
  const isHomeShell = location === "/" && !showPerfil;

  return (
    <div
      className={
        isHomeShell
          ? "min-h-screen flex flex-col bg-background"
          : "flex h-dvh max-h-dvh flex-col overflow-hidden bg-background"
      }
    >
      <div className="shrink-0">
        <Navbar
          user={user}
          onLogout={onLogout}
          isDarkMode={isDarkMode}
          toggleDarkMode={toggleDarkMode}
          openModules={openModules}
          onCloseModule={closeModule}
          onReorderModules={reorderOpenModules}
          onViewPerfil={() => setShowPerfil(true)}
          onDismissPerfil={() => setShowPerfil(false)}
          openModuleByRoute={openModuleByRoute}
          permittedModules={userAllowedModules}
        />
      </div>

      {isHomeShell ? (
        <>
          {renderModuleContent()}
          <footer className="shrink-0">
            <div className="px-6 py-2 text-center text-xs text-foreground/70">
              © 2026 ABA | Abertura Buenos Aires - Todos los derechos reservados.
            </div>
          </footer>
        </>
      ) : (
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
          {renderModuleContent()}
        </main>
      )}
    </div>
  );
}